import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ANNOUNCEMENT, ANNOUNCEMENT_SALES_OFFICE_CHECK, LIST_VIEW_LIMIT, SUCCESS_STATUS } from "../../constants";
import { LOADING_KEYS } from "../../constants/loadingKeys";
import { useListQuery } from "../../hooks/useListQuery";
import type { ApiEnvelope } from "../../types/api";
import * as api from "./api";
import type {
  AnnouncementDetail,
  AnnouncementItem,
  AnnouncementListData,
  CreateAnnouncementPayload,
  UpdateAnnouncementPayload,
} from "./types";

export const announcementQueryKeys = {
  list: (params: { offset: number; search?: string; sortType?: string }) => [ANNOUNCEMENT, params] as const,
  detail: (id: number | undefined) => [ANNOUNCEMENT, "detail", id] as const,
};

function formatAnnouncementItem(item: AnnouncementItem): AnnouncementItem {
  return {
    ...item,
    createdDate: item.createdDate ? format(new Date(item.createdDate), "MMMM dd,yyyy") : "",
    salesOffice:
      item.salesOffice && item.salesOffice !== ""
        ? (ANNOUNCEMENT_SALES_OFFICE_CHECK as Record<string, string>)[item.salesOffice.toLowerCase()] ?? ""
        : "",
  };
}

/**
 * List/search/sort/paginate for the Announcement admin list screen.
 */
export function useAnnouncementList(params: { offset: number; search?: string; sortType?: string }) {
  const query = useListQuery<ApiEnvelope<AnnouncementListData>>({
    url: ANNOUNCEMENT,
    method: "GET",
    limit: LIST_VIEW_LIMIT,
    offset: params.offset,
    search: params.search,
    sortType: params.sortType,
    loadingKey: LOADING_KEYS.GET_ANNOUNCEMENT,
  });

  const announcementList: ApiEnvelope<AnnouncementListData> = useMemo(() => {
    if (!query.data || query.data.status !== SUCCESS_STATUS) {
      return query.data ?? ({} as ApiEnvelope<AnnouncementListData>);
    }
    const raw = query.data.data ?? {};
    return {
      ...query.data,
      data: {
        ...raw,
        announcements: (raw.announcements ?? []).map(formatAnnouncementItem),
      },
    };
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...query, announcementList };
}

/** Fetches a single announcement by id. */
export function useAnnouncementDetail(id: number | undefined) {
  return useQuery({
    queryKey: announcementQueryKeys.detail(id),
    queryFn: () => api.fetchAnnouncementById(id as number),
    enabled: !!id,
    select: (envelope): AnnouncementDetail => (envelope.status === SUCCESS_STATUS ? envelope.data ?? {} : {}),
  });
}

/** Mutation: POST /announcements. Invalidates the list on success. */
export function useCreateAnnouncementMutation() {
  const queryClient = useQueryClient();
  return useMutation<ApiEnvelope<void>, Error, CreateAnnouncementPayload>({
    mutationFn: api.createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENT] });
    },
  });
}

/** Mutation: PUT /announcements/:id. Invalidates list and detail on success. */
export function useUpdateAnnouncementMutation() {
  const queryClient = useQueryClient();
  return useMutation<ApiEnvelope<void>, Error, UpdateAnnouncementPayload>({
    mutationFn: api.updateAnnouncement,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENT] });
      queryClient.invalidateQueries({
        queryKey: announcementQueryKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Mutation: DELETE /announcements/:id for each item (parallel, Promise.all).
 * Invalidates the list on success.
 */
export function useDeleteAnnouncementsMutation() {
  const queryClient = useQueryClient();
  return useMutation<ApiEnvelope<void>[], Error, Array<{ id: number }>>({
    mutationFn: api.deleteAnnouncements,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENT] });
    },
  });
}
